import { api } from "@/lib/api/client";

/** How the portal shows an invoice's state. `credited` = its credit notes
 *  cover the whole invoice; a credit note row itself is `credit_note`. */
export type PortalInvoiceStatus = "unpaid" | "paid" | "overdue" | "credited" | "credit_note";

/** One row of `/billing/portal/invoices/` — the tenant's booked, sent and
 *  paid invoices and credit notes (tenant admins only). */
export interface PortalInvoice {
  id: number;
  kind: "invoice" | "credit_note";
  /** The invoice number in the operator's accounting system; null until known. */
  number: string | null;
  /** A credit note: the number of the invoice it credits. */
  credits_number: string | null;
  /** First day of the billing month (ISO date). */
  period: string;
  invoice_date: string | null;
  due_date: string | null;
  /** Incl. VAT, as a decimal string; negative for a credit note. */
  amount: string;
  currency: string;
  status: PortalInvoiceStatus;
  has_pdf: boolean;
}

export type BookingCostReason = "booked_time" | "checked_in_time" | "late_cancellation" | "no_show_fee";

/** One charged meeting-room booking (#401 §8). Amounts are decimal strings, SEK excl. VAT. */
export interface BookingCostRow {
  charge_id: number;
  reference: string;
  booking_id: number;
  start: string;
  end: string;
  hours: string;
  room_id: number;
  room_name: string;
  /** The room's site, IANA name (#434): format start/end/cancelled_at in this zone, not the browser's.
   *  Optional only so a payload missing it still degrades to the browser's local time. */
  site_time_zone?: string;
  /** null in "my booking costs". */
  booked_by_id: number | null;
  booked_by_name: string | null;
  reason: BookingCostReason;
  checked_in_hours: string | null;
  cancelled_at: string | null;
  amount: string;
  /** false = credited back (no longer costs anything). */
  active: boolean;
  /** null in "my booking costs". */
  invoice_state: "not_invoiced" | "invoiced" | "credited" | null;
  /** The visible invoice that billed it; ``kind`` + ``number`` name its PDF download (``downloadInvoicePdf``). */
  invoice: { id: number; kind: PortalInvoice["kind"]; number: string | null; status: PortalInvoiceStatus; has_pdf: boolean } | null;
}

export interface BookingCosts {
  month: string;
  rows: BookingCostRow[];
  totals: { total: string; no_show_fees: string; late_cancellations: string };
  months: { month: string; total: string }[];
  members: { id: number; name: string }[];
  rooms: { id: number; name: string }[];
}

export interface BookingCostFilters {
  month: string;
  member: number | null;
  room: number | null;
  reason: BookingCostReason | null;
}

export const billingApi = {
  listInvoices: () => api.get<PortalInvoice[]>("/billing/portal/invoices/"),

  /** The PDF, fetched live from the accounting system by the backend. */
  getInvoicePdf: (id: number) =>
    api.get<Blob>(`/billing/portal/invoices/${id}/pdf/`, { responseType: "blob" }),

  /** Tenant admins: every charged booking of the company that month. */
  getBookingCosts: ({ month, member, room, reason }: BookingCostFilters) =>
    api.get<BookingCosts>("/billing/portal/booking-costs/", {
      params: { month, ...(member ? { member } : {}), ...(room ? { room } : {}), ...(reason ? { reason } : {}) },
    }),

  /** Any member: their own charged bookings that month. */
  getMyBookingCosts: (month: string) => api.get<BookingCosts>("/billing/portal/my-booking-costs/", { params: { month } }),
};
